import React from 'react';
import { useLocation } from 'react-router-dom';

import { useBackgroundAudio } from '../../audio/BackgroundAudioProvider';
import { useHomeSection } from '../../lib/homeSection';
import classes from './AudioToggle.module.css';

// Global audio toggle. Owns no audio state — defers entirely to
// BackgroundAudioProvider via useBackgroundAudio().
//
// Variant resolution:
//   "full"     — label + waveform. Used on the hero/landing section of "/".
//   "compact"  — waveform only; label revealed on hover / keyboard focus.
//   "menu"     — full-width row (label + waveform + on/off state). Used inside the
//                mobile burger menu (MobileMenuOverlay). The floating instance is
//                hidden on mobile via CSS so only this one shows there.
//
// Auto-detected from the current route and (when on "/") the published
// home active section. Pass `variant` explicitly to override.
function AudioToggle({ variant }) {
    const { isPlaying, toggleAudio } = useBackgroundAudio();
    const { pathname } = useLocation();
    const homeSection = useHomeSection();

    let resolved = variant;
    if (!resolved) {
        // homeSection === null covers the brief window before Home mounts and
        // after it unmounts. Pre-mount on "/" we want full; post-unmount on a
        // non-home route the pathname check below will route us to compact.
        const onLanding =
            pathname === '/' && (homeSection === 0 || homeSection === null);
        resolved = onLanding ? 'full' : 'compact';
    }

    let variantClass;
    if (resolved === 'menu') variantClass = classes.Menu;
    else if (resolved === 'compact') variantClass = classes.Compact;
    else variantClass = classes.Full;
    const stateClass = isPlaying ? classes.Playing : classes.Paused;
    const ariaLabel = isPlaying ? 'Disable audio' : 'Enable audio';

    // On the home route the mobile audio control lives in the burger menu, so the floating
    // instance is hidden there (CSS). On other routes (e.g. /work/:slug, which has no menu)
    // the floating control must stay so audio is still toggleable on mobile.
    const onHomeClass = pathname === '/' ? classes.OnHome : '';

    return (
        <button
            type="button"
            data-audio-toggle
            className={[classes.AudioToggle, variantClass, stateClass, onHomeClass].join(' ').trim()}
            onClick={toggleAudio}
            aria-pressed={isPlaying}
            aria-label={ariaLabel}
        >
            <span className={classes.Label}>Audio</span>
            <svg
                className={classes.Wave}
                viewBox="0 0 64 32"
                width="64"
                height="32"
                aria-hidden="true"
                focusable="false"
            >
                <path
                    className={classes.WaveGhost}
                    d="M0 16 Q 4 13, 8 16 T 16 16 T 24 16 T 32 16 T 40 16 T 48 16 T 56 16 T 64 16"
                />
                <path
                    className={classes.WavePath}
                    d="M0 16 Q 4 8, 8 16 T 16 16 T 24 16 T 32 16 T 40 16 T 48 16 T 56 16 T 64 16"
                />
            </svg>
            {/* On/off word — only rendered visibly in the "menu" variant (CSS). */}
            <span className={classes.State} aria-hidden="true">{isPlaying ? 'On' : 'Off'}</span>
        </button>
    );
}

export default AudioToggle;
